import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, } from "react";
import { createPortal } from "react-dom";
import { Icon } from "../Icon.js";
import { fill, useTexts } from "../texts.js";
import { Button, Kbd } from "../ui.js";
import { cx, isTypingTarget } from "../util.js";
const Ctx = createContext(null);
/** Start and stop tours; needs a TourProvider above. */
export function useTour() {
    const c = useContext(Ctx);
    if (!c)
        throw new Error("TourProvider missing");
    return c;
}
/**
 * Guided tours: a spotlight on a `data-tour` anchor and a card that explains
 * it. The tour owns the keyboard while it runs (→ ← Enter Esc), clicks
 * outside the highlighted area are blocked, and non-interactive areas are
 * locked, so nothing is triggered by accident.
 *
 * The app brings the screen into place for each step in `prepare` (route,
 * open a drawer or tab); the anchor is searched for after it resolved.
 */
export function TourProvider({ tours, children, prepare, beforeStart, onFinish, onStop, }) {
    const [run, setRun] = useState(null);
    const cb = useRef({ prepare, beforeStart, onFinish, onStop });
    cb.current = { prepare, beforeStart, onFinish, onStop };
    const stop = useCallback(() => {
        setRun(null);
        cb.current.onStop?.();
    }, []);
    const start = useCallback((id) => {
        cb.current.beforeStart?.();
        window.setTimeout(() => setRun({ id, i: 0, dir: 1 }), 60);
    }, []);
    const api = useMemo(() => ({ active: run?.id ?? null, start, stop }), [run?.id, start, stop]);
    const tour = run ? tours.find((t) => t.id === run.id) : undefined;
    return (_jsxs(Ctx.Provider, { value: api, children: [children, run && tour && (_jsx(TourRunner, { tour: tour, index: run.i, dir: run.dir, go: (i, dir) => setRun((r) => (r ? { ...r, i, dir } : r)), prepare: (step, ctx) => cb.current.prepare?.(step, ctx), onFinish: () => {
                    cb.current.onFinish?.(tour);
                    stop();
                }, onClose: stop }, run.id))] }));
}
// ---------------------------------------------------------------------------
const sleep = (ms) => new Promise((r) => window.setTimeout(r, ms));
const targetsOf = (s) => (s.target ? (Array.isArray(s.target) ? s.target : [s.target]) : []);
function findTarget(targets) {
    for (const t of targets) {
        for (const el of document.querySelectorAll(`[data-tour="${t}"]`)) {
            const r = el.getBoundingClientRect();
            if (r.width > 0 && r.height > 0)
                return el;
        }
    }
    return null;
}
const sameRect = (a, b) => !!a && !!b && a.top === b.top && a.left === b.left && a.width === b.width && a.height === b.height;
function TourRunner({ tour, index, dir, go, prepare, onFinish, onClose, }) {
    const step = tour.steps[index];
    const total = tour.steps.length;
    const last = index === total - 1;
    const [ready, setReady] = useState(false);
    const [rect, setRect] = useState(null);
    const elRef = useRef(null);
    const next = useCallback(() => (last ? onFinish() : go(index + 1, 1)), [last, onFinish, go, index]);
    const back = useCallback(() => index > 0 && go(index - 1, -1), [go, index]);
    // prepare the step (app: route, open UI), then wait for the anchor
    useEffect(() => {
        let cancelled = false;
        setReady(false);
        setRect(null);
        elRef.current = null;
        void (async () => {
            try {
                await prepare(step, { tour, index, dir });
            }
            catch {
                // a failed preparation shows the step without its anchor
            }
            const targets = targetsOf(step);
            if (!targets.length) {
                if (!cancelled)
                    setReady(true);
                return;
            }
            let el = null;
            for (let t = 0; t < 30 && !cancelled; t++) {
                el = findTarget(targets);
                if (el)
                    break;
                await sleep(100);
            }
            if (cancelled)
                return;
            if (!el && step.optional) {
                // nothing to show here: move on in the direction the user was going
                if (dir === 1) {
                    if (last)
                        onFinish();
                    else
                        go(index + 1, 1);
                }
                else if (index > 0)
                    go(index - 1, -1);
                else
                    go(index + 1, 1);
                return;
            }
            if (el) {
                const r = el.getBoundingClientRect();
                const vh = window.innerHeight;
                // on a phone the card is a sheet over the lower (or upper) part: the
                // anchor goes to the upper half when it fits there
                const narrow = isNarrow();
                const fits = narrow ? r.top >= 70 && (r.bottom <= vh * 0.5 || r.height > vh * 0.4) : r.top >= 70 && r.bottom <= vh - 12;
                if (!fits) {
                    el.scrollIntoView({ block: narrow || r.height > vh * 0.6 ? "start" : "center", behavior: "smooth" });
                    await sleep(320);
                    if (narrow) {
                        window.scrollBy({ top: -80, behavior: "instant" });
                        await sleep(40);
                    }
                }
            }
            if (cancelled)
                return;
            elRef.current = el;
            setRect(el ? el.getBoundingClientRect() : null);
            setReady(true);
        })();
        return () => {
            cancelled = true;
        };
        // a step is identified by tour + index; the rest is read at that moment
    }, [tour.id, index]);
    // follow the anchor (scroll, resize, re-render of the anchor)
    useEffect(() => {
        if (!ready || !targetsOf(step).length)
            return;
        const update = () => {
            let el = elRef.current;
            if (!el || !el.isConnected) {
                el = findTarget(targetsOf(step));
                elRef.current = el;
            }
            const r = el ? el.getBoundingClientRect() : null;
            setRect((prev) => (sameRect(prev, r) ? prev : r));
        };
        window.addEventListener("resize", update);
        window.addEventListener("scroll", update, true);
        const iv = window.setInterval(update, 300);
        return () => {
            window.removeEventListener("resize", update);
            window.removeEventListener("scroll", update, true);
            window.clearInterval(iv);
        };
    }, [ready, step]);
    // keyboard: the tour owns the keyboard while it runs (no accidental shortcuts)
    useEffect(() => {
        const h = (e) => {
            if (e.key === "Tab") {
                // focus stays in the card (and the highlighted part of an interactive step)
                const card = document.querySelector("[data-tour-card]");
                if (!card)
                    return;
                const anchor = step.interactive ? elRef.current : null;
                const list = [
                    ...(anchor && anchor.matches(FOCUSABLE) ? [anchor] : []),
                    ...(anchor ? [...anchor.querySelectorAll(FOCUSABLE)] : []),
                    ...card.querySelectorAll(FOCUSABLE),
                ].filter((el) => el.getClientRects().length > 0);
                if (!list.length)
                    return;
                e.preventDefault();
                e.stopPropagation();
                const at = list.indexOf(document.activeElement);
                const to = at < 0 ? 0 : (at + (e.shiftKey ? -1 : 1) + list.length) % list.length;
                list[to]?.focus();
                return;
            }
            // typing in a highlighted input stays typing
            if (e.key !== "Escape" && isTypingTarget(e.target))
                return;
            const inCard = e.target?.closest?.("[data-tour-card]");
            const onButton = inCard && e.target.tagName === "BUTTON";
            e.stopPropagation();
            if (e.key === "Escape") {
                e.preventDefault();
                onClose();
            }
            else if (e.key === "ArrowRight" || (e.key === "Enter" && !onButton)) {
                e.preventDefault();
                next();
            }
            else if (e.key === "ArrowLeft") {
                e.preventDefault();
                back();
            }
        };
        window.addEventListener("keydown", h, true);
        return () => window.removeEventListener("keydown", h, true);
    }, [next, back, onClose, step]);
    return createPortal(_jsx(TourLayer, { step: step, rect: ready ? rect : null, ready: ready, children: _jsx(TourCard, { step: step, tour: tour, index: index, total: total, last: last, ready: ready, rect: ready ? rect : null, onNext: next, onBack: back, onClose: onClose }) }), document.body);
}
const PAD = 6;
/** Below 640 px the step card is a full-width sheet (bottom, or top when the anchor is low). */
const isNarrow = () => window.innerWidth < 640;
const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
// colours come from theme.css (--enlixy-tour-dim, --enlixy-tour-ring)
const DIM = "var(--enlixy-tour-dim)";
const ring = (pct) => `color-mix(in srgb, var(--enlixy-tour-ring) ${pct}%, transparent)`;
function TourLayer({ step, rect, ready, children, }) {
    const t = useTexts();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const block = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };
    if (!rect) {
        return (_jsxs("div", { className: "fixed inset-0 z-[90]", onMouseDown: block, onClick: block, children: [_jsx("div", { className: "absolute inset-0 transition-opacity duration-300", style: { background: DIM, opacity: ready ? 1 : 0.6 } }), children] }));
    }
    const hole = {
        top: Math.max(4, rect.top - PAD),
        left: Math.max(4, rect.left - PAD),
        right: Math.min(vw - 4, rect.right + PAD),
        bottom: Math.min(vh - 4, rect.bottom + PAD),
    };
    const shield = "fixed z-[90]";
    const shields = [
        { top: 0, left: 0, width: vw, height: hole.top },
        { top: hole.bottom, left: 0, width: vw, height: vh - hole.bottom },
        { top: hole.top, left: 0, width: hole.left, height: hole.bottom - hole.top },
        { top: hole.top, left: hole.right, width: vw - hole.right, height: hole.bottom - hole.top },
    ];
    return (_jsxs(_Fragment, { children: [shields.map((s, i) => (_jsx("div", { className: shield, style: s, onMouseDown: block, onClick: block }, i))), !step.interactive && (_jsx("div", { className: shield, style: {
                    top: hole.top,
                    left: hole.left,
                    width: hole.right - hole.left,
                    height: hole.bottom - hole.top,
                }, onMouseDown: block, onClick: block, title: t.tourLocked })), _jsx("div", { "aria-hidden": "true", className: "tour-spotlight pointer-events-none fixed z-[90] rounded-2xl", style: {
                    top: hole.top,
                    left: hole.left,
                    width: hole.right - hole.left,
                    height: hole.bottom - hole.top,
                    boxShadow: `0 0 0 3px ${ring(95)}, 0 0 0 9px ${ring(18)}, 0 0 0 200vmax ${DIM}`,
                } }), children] }));
}
function placeCard(rect, cw, ch, pref) {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const m = 12;
    const gap = 18;
    const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
    if (!rect)
        return {
            top: clamp((vh - ch) / 2, m, vh - ch - m),
            left: clamp((vw - cw) / 2, m, vw - cw - m),
        };
    const order = [...new Set([...(pref ? [pref] : []), "right", "left", "bottom", "top"])];
    for (const p of order) {
        if (p === "right" && rect.right + gap + cw <= vw - m)
            return { left: rect.right + gap, top: clamp(rect.top, m, vh - ch - m) };
        if (p === "left" && rect.left - gap - cw >= m)
            return { left: rect.left - gap - cw, top: clamp(rect.top, m, vh - ch - m) };
        if (p === "bottom" && rect.bottom + gap + ch <= vh - m)
            return { top: rect.bottom + gap, left: clamp(rect.left + rect.width / 2 - cw / 2, m, vw - cw - m) };
        if (p === "top" && rect.top - gap - ch >= m)
            return { top: rect.top - gap - ch, left: clamp(rect.left + rect.width / 2 - cw / 2, m, vw - cw - m) };
    }
    // the anchor fills the screen: dock the card in the lower right corner
    return { top: vh - ch - m, left: vw - cw - m };
}
function TourCard({ step, tour, index, total, last, ready, rect, onNext, onBack, onClose, }) {
    const t = useTexts();
    const ref = useRef(null);
    const nextBtn = useRef(null);
    const [pos, setPos] = useState(null);
    const [sheet, setSheet] = useState(null);
    useLayoutEffect(() => {
        const el = ref.current;
        if (!el)
            return;
        if (isNarrow()) {
            // a sheet never covers the anchor's centre
            const side = rect && rect.top + rect.height / 2 > window.innerHeight * 0.5 ? "top" : "bottom";
            setSheet(side);
            return;
        }
        setSheet(null);
        const p = placeCard(rect, el.offsetWidth, el.offsetHeight, step.placement);
        setPos((old) => (old && old.top === p.top && old.left === p.left ? old : p));
    }, [rect, step, ready]);
    useEffect(() => {
        if (ready)
            nextBtn.current?.focus({ preventScroll: true });
    }, [ready, index]);
    const titleId = `tour-title-${tour.id}-${step.id}`;
    return (_jsxs("div", { ref: ref, "data-tour-card": true, role: "dialog", "aria-modal": "true", "aria-labelledby": titleId, "data-tour-sheet": sheet ?? undefined, className: cx("fixed z-[91] flex flex-col overflow-hidden bg-surface text-fg shadow-2xl ring-1 ring-white/20 transition-[top,left,opacity] duration-300 ease-out", sheet
            ? cx("inset-x-0 max-h-[55dvh] w-full", sheet === "bottom" ? "bottom-0 rounded-t-[22px] pb-[env(safe-area-inset-bottom)]" : "top-0 rounded-b-[22px] pt-[env(safe-area-inset-top)]")
            : "max-h-[min(78vh,640px)] w-[min(410px,calc(100vw-24px))] rounded-[22px]", ready && (pos || sheet) ? "opacity-100" : "opacity-0"), style: sheet ? undefined : (pos ?? { top: -9999, left: -9999 }), onMouseDown: (e) => e.stopPropagation(), children: [_jsx("div", { className: "h-1 bg-surface-3", children: _jsx("div", { className: "h-full rounded-r-full bg-gradient-to-r from-brand-bright to-brand-light transition-[width] duration-500", style: { width: `${((index + 1) / total) * 100}%` } }) }), _jsxs("div", { className: "flex items-start gap-3 px-5 pt-4", children: [_jsx("span", { className: "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-bright to-brand-light text-on-brand shadow-sm", children: _jsx(Icon, { name: step.icon ?? tour.icon, size: 18 }) }), _jsxs("div", { className: "min-w-0 flex-1", children: [_jsxs("p", { className: "text-[11px] font-medium text-muted", children: [tour.title, " \u00B7 ", fill(t.tourStepOf, { n: index + 1, total })] }), _jsx("h2", { id: titleId, className: "text-[16px] leading-snug font-semibold", children: step.title })] }), _jsx("button", { type: "button", onClick: onClose, "aria-label": t.tourClose, title: t.tourCloseTitle, className: "-mr-1 inline-flex items-center justify-center rounded-lg p-1 text-muted hover:bg-hover hover:text-fg pointer-coarse:min-h-11 pointer-coarse:min-w-11", children: _jsx(Icon, { name: "x", size: 16 }) })] }), _jsxs("div", { className: "scroll-thin min-h-0 flex-1 space-y-2.5 overflow-y-auto px-5 pt-3 pb-4 text-[13.5px] leading-relaxed text-fg-2", children: [step.body.map((p, i) => (_jsx("p", { children: p }, i))), step.points && (_jsx("dl", { className: "space-y-1.5 rounded-xl bg-surface-2 p-3 ring-1 ring-line/60", children: step.points.map(([term, text]) => (_jsxs("div", { className: "text-[13px] leading-snug", children: [_jsxs("dt", { className: "inline font-semibold text-fg", children: [term, ": "] }), _jsx("dd", { className: "inline", children: text })] }, term))) })), step.tryIt && (_jsxs("p", { className: "flex items-start gap-2 rounded-xl bg-accent/10 px-3 py-2 text-[13px] text-fg ring-1 ring-accent/25", children: [_jsx(Icon, { name: "play", size: 13, className: "mt-1 shrink-0 text-accent" }), _jsxs("span", { children: [_jsxs("span", { className: "font-semibold", children: [t.tourTryIt, " "] }), step.tryIt] })] })), step.tip && (_jsxs("p", { className: "flex items-start gap-2 text-[12.5px] text-fg-3", children: [_jsx(Icon, { name: "info", size: 13, className: "mt-0.5 shrink-0 text-muted" }), step.tip] })), step.keys && (_jsx("ul", { className: "flex flex-wrap gap-x-4 gap-y-1.5 pt-0.5", children: step.keys.map(([keys, label]) => (_jsxs("li", { className: "flex items-center gap-1.5 text-xs text-fg-3", children: [keys.map((k) => (_jsx(Kbd, { children: k }, k))), _jsx("span", { children: label })] }, label))) }))] }), _jsxs("div", { className: "flex items-center gap-2 border-t border-line/70 bg-surface-2/60 px-4 py-3", children: [_jsx("span", { className: "flex gap-1", "aria-hidden": "true", children: Array.from({ length: total }, (_, i) => (_jsx("span", { className: cx("h-1.5 rounded-full transition-all duration-300", i === index ? "w-4 bg-accent" : i < index ? "w-1.5 bg-accent/50" : "w-1.5 bg-line") }, i))) }), _jsx("span", { className: "flex-1" }), index > 0 && (_jsx(Button, { size: "sm", variant: "ghost", icon: "chevronLeft", onClick: onBack, children: t.back })), _jsxs(Button, { ref: nextBtn, size: "sm", variant: "primary", onClick: onNext, className: "min-w-[86px]", children: [last ? t.done : t.next, !last && _jsx(Icon, { name: "chevronRight", size: 13 })] })] })] }));
}
